import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";

interface FormData {
  name: string;
  phone: string;
  email: string;
  cpf: string;
  situation: string;
  message: string;
}

interface ContactInfo {
  icon: string;
  title: string;
  value: string;
  description: string;
}

const ContactSection = () => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    phone: "",
    email: "",
    cpf: "",
    situation: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const contactInfo: ContactInfo[] = [
    {
      icon: "Phone",
      title: "Telefone",
      value: "(33) 99707-0278",
      description: "Seg à Sex: 8h às 18h",
    },
    {
      icon: "Mail",
      title: "E-mail",
      value: "tjepfinanceira@gmail.com",
      description: "Resposta em até 2h",
    },
    {
      icon: "MapPin",
      title: "Endereço",
      value: "Rua Antônio Cimini, 130 - Caratinga/MG",
      description: "CEP: 01310-100",
    },
    {
      icon: "Clock",
      title: "Horário",
      value: "Segunda à Sexta",
      description: "8h às 18h",
    },
  ];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const formatCPF = (value: string) => {
    const numbers = value?.replace(/\D/g, "");
    return numbers?.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  };

  const formatPhone = (value: string) => {
    const numbers = value?.replace(/\D/g, "");
    return numbers?.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  };

  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e?.target?.value);
    setFormData((prev) => ({
      ...prev,
      cpf: formatted,
    }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e?.target?.value);
    setFormData((prev) => ({
      ...prev,
      phone: formatted,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Redirect to WhatsApp with form data
    const message = encodeURIComponent(
      `Olá! Solicito análise financeira gratuita.\n\n` +
        `Nome: ${formData?.name}\n` +
        `Telefone: ${formData?.phone}\n` +
        `E-mail: ${formData?.email}\n` +
        `CPF: ${formData?.cpf}\n` +
        `Situação: ${formData?.situation}\n` +
        `Mensagem: ${formData?.message || "Não informado"}`,
    );

    window.open(`https://wa.me/553397070278?text=${message}`, "_blank");

    setShowSuccess(true);
    setIsSubmitting(false);

    // Reset form after success
    setTimeout(() => {
      setFormData({
        name: "",
        phone: "",
        email: "",
        cpf: "",
        situation: "",
        message: "",
      });
      setShowSuccess(false);
    }, 3000);
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      "Olá! Gostaria de falar com um especialista sobre soluções financeiras.",
    );
    window.open(`https://wa.me/553397070278?text=${message}`, "_blank");
  };

  return (
    <section id="contato" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="MessageSquare" size={16} />
            <span>Entre em Contato</span>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Solicite Sua Análise Financeira Gratuita
          </h2>

          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Preencha o formulário abaixo e nossa equipe entrará em contato em
            até 24 horas para realizar sua análise financeira personalizada e
            gratuita
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <div className="bg-gradient-to-br from-slate-50 to-white rounded-3xl p-8 md:p-12 shadow-subtle border">
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-foreground mb-2">
                Formulário de Contato
              </h3>
              <p className="text-muted-foreground">
                Todas as informações são tratadas com total sigilo e segurança
              </p>
            </div>

            {showSuccess ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="CheckCircle" size={32} className="text-success" />
                </div>
                <h4 className="text-xl font-bold text-foreground mb-2">
                  Solicitação Enviada!
                </h4>
                <p className="text-muted-foreground mb-4">
                  Você será redirecionado para o WhatsApp para continuar o
                  atendimento
                </p>
                <div className="flex items-center justify-center space-x-2 text-sm text-success">
                  <Icon name="Clock" size={16} />
                  <span>Resposta garantida em 24h</span>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    label="Nome Completo"
                    type="text"
                    name="name"
                    value={formData?.name}
                    onChange={handleInputChange}
                    placeholder="Seu nome completo"
                    required
                  />

                  <Input
                    label="Telefone/WhatsApp"
                    type="tel"
                    name="phone"
                    value={formData?.phone}
                    onChange={handlePhoneChange}
                    placeholder="(11) 99999-9999"
                    required
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    label="E-mail"
                    type="email"
                    name="email"
                    value={formData?.email}
                    onChange={handleInputChange}
                    placeholder="seu@email.com"
                    required
                  />

                  <Input
                    label="CPF"
                    type="text"
                    name="cpf"
                    value={formData?.cpf}
                    onChange={handleCPFChange}
                    placeholder="000.000.000-00"
                    maxLength={14}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Situação Atual *
                  </label>
                  <select
                    name="situation"
                    value={formData?.situation}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors duration-200"
                  >
                    <option value="">Selecione sua situação</option>
                    <option value="aposentado">Aposentado(a)</option>
                    <option value="pensionista">Pensionista</option>
                    <option value="clt">Trabalhador CLT</option>
                    <option value="autonomo">Autônomo</option>
                    <option value="outros">Outros</option>
                  </select>
                </div>

                <Input
                  label="Mensagem (Opcional)"
                  type="text"
                  name="message"
                  value={formData?.message}
                  onChange={handleInputChange}
                  placeholder="Conte-nos mais sobre sua situação ou dúvidas..."
                />

                {/* Privacy Notice */}
                <div className="bg-primary/5 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon
                      name="Shield"
                      size={20}
                      className="text-primary flex-shrink-0 mt-0.5"
                    />
                    <div className="text-sm">
                      <p className="text-foreground font-medium mb-1">
                        Seus dados estão protegidos
                      </p>
                      <p className="text-muted-foreground">
                        Seguimos rigorosamente a LGPD e não compartilhamos suas
                        informações com terceiros. Utilizamos apenas para
                        análise e contato sobre nossos serviços.
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  fullWidth
                  loading={isSubmitting}
                  iconName="Send"
                  iconPosition="right"
                  className="text-lg py-4 shadow-cta"
                >
                  {isSubmitting ? "Enviando..." : "Solicitar Análise Gratuita"}
                </Button>
              </form>
            )}
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            {/* Contact Details */}
            <div className="bg-white rounded-2xl p-8 shadow-subtle border">
              <h3 className="text-xl font-bold text-foreground mb-6">
                Informações de Contato
              </h3>

              <div className="space-y-6">
                {contactInfo?.map((info, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon
                        name={info?.icon as any}
                        size={20}
                        className="text-primary"
                      />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground">
                        {info?.title}
                      </h4>
                      <p className="text-foreground">{info?.value}</p>
                      <p className="text-sm text-muted-foreground">
                        {info?.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* WhatsApp CTA */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-8 text-white">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <Icon name="MessageCircle" size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Atendimento Imediato</h3>
                  <p className="text-green-100">
                    Fale conosco agora pelo WhatsApp
                  </p>
                </div>
              </div>

              <p className="text-green-100 mb-6">
                Prefere falar diretamente? Nossa equipe está online e pronta
                para atendê-lo com toda atenção que você merece.
              </p>

              <Button
                variant="secondary"
                size="lg"
                fullWidth
                onClick={handleWhatsAppClick}
                iconName="MessageCircle"
                iconPosition="left"
                className="bg-white text-green-600 hover:bg-green-50"
              >
                Chamar no WhatsApp
              </Button>
            </div>

            {/* Map */}
            <div className="bg-white rounded-2xl p-8 shadow-subtle border">
              <h3 className="text-xl font-bold text-foreground mb-4">
                Nossa Localização
              </h3>

              <div className="aspect-video rounded-lg overflow-hidden">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title="TJEP Soluções Financeiras - Rua Antônio Cimini, 130 - Caratinga/MG"
                  referrerPolicy="no-referrer-when-downgrade"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3754.2043475548853!2d-42.1395760173608!3d-19.78893138638702!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xba90e97baf284d%3A0x4ba11786e677dfb0!2sR.%20Ant%C3%B4nio%20Cimini%2C%20130%20-%20Santa%20Zita%2C%20Caratinga%20-%20MG%2C%2035300-275!5e0!3m2!1sen!2sbr!4v1760899252838!5m2!1sen!2sbr&z=15&output=embed"
                  className="border-0"
                />
              </div>

              <div className="mt-4 text-sm text-muted-foreground">
                <p>Rua Antônio Cimini, 130, Santa Zita</p>
                <p>Caratiga/MG - CEP: 35300-275</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
